import React from 'react';

/**
 * Draws the classic bracket "elbow" connectors between one round's matches
 * and the next round's — pure math from match count and shared column
 * height, no DOM measurement needed. BracketBoard already guarantees every
 * match card lands at y = totalHeight * (index + 0.5) / matchCount via
 * `justify-content: space-around`, so a same-height SVG overlay using that
 * exact formula lines up with the real cards automatically.
 *
 * `matchCount` is always the PAIRING side's count (the round with more
 * matches). `reversed` mirrors the whole drawing horizontally for the case
 * where that pairing round sits on the right instead of the left — e.g. a
 * mirrored/reversed bracket segment where rounds were flipped so the
 * bracket visually converges leftward instead of rightward.
 */
const BracketConnector = ({ matchCount, totalHeight, width, colorClassName, reversed = false }) => {
  const pairs = matchCount / 2;
  const yFor = (i) => (totalHeight * (i + 0.5)) / matchCount;
  const midX = width / 2;
  const nearX = reversed ? width : 0; // edge where the pairing round's stubs start
  const farX = reversed ? 0 : width; // edge where the merged line lands (the single next-round card)

  return (
    <svg width={width} height={totalHeight} className={colorClassName}>
      {Array.from({ length: pairs }, (_, p) => {
        const yTop = yFor(2 * p);
        const yBottom = yFor(2 * p + 1);
        const yMid = (yTop + yBottom) / 2;
        return (
          <g key={p} stroke="currentColor" strokeWidth="1.5" fill="none">
            <line x1={nearX} y1={yTop} x2={midX} y2={yTop} />
            <line x1={nearX} y1={yBottom} x2={midX} y2={yBottom} />
            <line x1={midX} y1={yTop} x2={midX} y2={yBottom} />
            <line x1={midX} y1={yMid} x2={farX} y2={yMid} />
          </g>
        );
      })}
    </svg>
  );
};

/**
 * Same classic 2-into-1 elbow shape, but for composing layouts that can't
 * rely on BracketBoard's own uniform space-around spacing — e.g.
 * RegionalBracketView, where the two source cards (two regions' Final
 * matches) can sit at different, independently-computed Y positions rather
 * than positions derived from a shared matchCount/totalHeight formula.
 */
// `yExit` is where the merged line leaves toward the destination card — it
// defaults to the natural midpoint of the two sources, but can be overridden
// when the destination isn't at that midpoint (e.g. a destination card whose
// own position is fixed by an independent stack layout rather than by these
// two sources' geometry). The vertical bar always spans the real yTop/yBottom
// so the two source stubs are still visually honest.
export const ElbowPairConnector = ({ yTop, yBottom, yExit, height, width, colorClassName, reversed = false }) => {
  const midX = width / 2;
  const yMid = yExit ?? (yTop + yBottom) / 2;
  const nearX = reversed ? width : 0;
  const farX = reversed ? 0 : width;

  return (
    <svg width={width} height={height} className={colorClassName}>
      <g stroke="currentColor" strokeWidth="1.5" fill="none">
        <line x1={nearX} y1={yTop} x2={midX} y2={yTop} />
        <line x1={nearX} y1={yBottom} x2={midX} y2={yBottom} />
        <line x1={midX} y1={yTop} x2={midX} y2={yBottom} />
        <line x1={midX} y1={yMid} x2={farX} y2={yMid} />
      </g>
    </svg>
  );
};

/**
 * A single independent line from one Y to another, elbowed through the
 * midpoint — for asymmetric connections where the two ends aren't "a pair
 * converging to one destination" but two SEPARATE single links approaching
 * the same destination from opposite sides (e.g. Semifinal 1 from the left
 * and Semifinal 2 from the right, both feeding into a centered Championship
 * card — each is its own line, not a shared-vertical-bar pair).
 */
export const SingleLineConnector = ({ yFrom, yTo, height, width, colorClassName, reversed = false }) => {
  const midX = width / 2;
  const x0 = reversed ? width : 0;
  const x1 = reversed ? 0 : width;

  return (
    <svg width={width} height={height} className={colorClassName}>
      <g stroke="currentColor" strokeWidth="1.5" fill="none">
        <line x1={x0} y1={yFrom} x2={midX} y2={yFrom} />
        <line x1={midX} y1={yFrom} x2={midX} y2={yTo} />
        <line x1={midX} y1={yTo} x2={x1} y2={yTo} />
      </g>
    </svg>
  );
};

export default BracketConnector;
